import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  groupKitSkillControllerCreate,
  groupKitSkillControllerDeleteSkill,
  groupKitSkillControllerGetAll,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

export const useSkillsData = (
  groupUuid: string | null,
  kitUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const { data: skillsResponse } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    queryKey: queryKeys.kitSkills(groupUuid!, kitUuid!),
    queryFn: async () => {
      const response = await groupKitSkillControllerGetAll({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      });
      return response.data;
    },
  });

  const skills = skillsResponse?.data || [];

  const { mutateAsync: createSkill, isPending: isCreating } = useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      content: string;
      category?: string | null;
    }) => {
      if (!groupUuid || !kitUuid) {
        throw new Error("Group UUID and Kit UUID are required");
      }

      const response = await groupKitSkillControllerCreate({
        path: {
          groupUuid,
          kitUuid,
        },
        body: {
          name: data.name,
          description: data.description || "",
          content: data.content,
          category: data.category,
        },
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitSkills(groupUuid!, kitUuid!),
      });
    },
    onError: (error) => {
      console.error("Failed to create skill:", error);
      toast.error("Failed to create skill");
    },
  });

  const { mutateAsync: deleteSkill, isPending: isDeleting } = useMutation({
    mutationFn: async (skillUuid: string) => {
      if (!groupUuid || !kitUuid) {
        throw new Error("Group UUID and Kit UUID are required");
      }

      await groupKitSkillControllerDeleteSkill({
        path: {
          groupUuid,
          kitUuid,
          skillUuid,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitSkills(groupUuid!, kitUuid!),
      });
      toast.success("Skill deleted successfully");
    },
    onError: (error) => {
      console.error("Failed to delete skill:", error);
      toast.error("Failed to delete skill");
    },
  });

  return {
    skills,
    createSkill,
    deleteSkill,
    isCreating,
    isDeleting,
  };
};
